import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader, AlertTriangle, UserCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { getReferrerById, createReferrer, updateReferrer, checkDuplicates } from '../api/referrers';
import LoadingSpinner from '../components/common/LoadingSpinner';

const ReferrerFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    address: '',
    commission_type: 'percentage',
    commission_rate: 5,
    fixed_commission_amount: 0,
    bank_name: '',
    account_number: '',
    account_name: '',
    is_active: true
  });

  const [errors, setErrors] = useState({});

  // Duplicate detection state
  const [duplicates, setDuplicates] = useState([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const duplicateCheckTimerRef = useRef(null);

  const fetchReferrer = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReferrerById(id);
      setFormData({
        full_name: data.full_name || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        commission_type: data.commission_type || 'percentage',
        commission_rate: data.commission_rate || 5,
        fixed_commission_amount: data.fixed_commission_amount || 0,
        bank_name: data.bank_name || '',
        account_number: data.account_number || '',
        account_name: data.account_name || '',
        is_active: data.is_active !== false
      });
    } catch (error) {
      toast.error('Failed to fetch referrer details');
      navigate('/referrers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Fetch referrer data if editing
  useEffect(() => {
    if (isEditMode) {
      fetchReferrer();
    }
  }, [isEditMode, fetchReferrer]);

  // Check for duplicates
  const performDuplicateCheck = useCallback(
    async (fullName, phone) => {
      // Don't check if both fields are empty
      if (!fullName.trim() && !phone.trim()) {
        setDuplicates([]);
        return;
      }

      setCheckingDuplicates(true);
      try {
        const result = await checkDuplicates(
          fullName.trim() || null,
          phone.trim() || null,
          isEditMode ? id : null // Exclude current referrer when editing
        );
        setDuplicates(result.duplicates || []);
      } catch (error) {
        setDuplicates([]);
      } finally {
        setCheckingDuplicates(false);
      }
    },
    [id, isEditMode]
  );

  // Debounced duplicate check effect
  useEffect(() => {
    // Clear existing timer
    if (duplicateCheckTimerRef.current) {
      clearTimeout(duplicateCheckTimerRef.current);
    }

    // Set new timer for debounced check
    duplicateCheckTimerRef.current = setTimeout(() => {
      performDuplicateCheck(formData.full_name, formData.phone);
    }, 500); // 500ms debounce

    // Cleanup
    return () => {
      if (duplicateCheckTimerRef.current) {
        clearTimeout(duplicateCheckTimerRef.current);
      }
    };
  }, [formData.full_name, formData.phone, performDuplicateCheck]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^0[7-9][0-1]\d{8}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Invalid Nigerian phone number (e.g., 08012345678)';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.commission_type) {
      newErrors.commission_type = 'Commission type is required';
    }

    if (formData.commission_type === 'percentage') {
      const rate = parseFloat(formData.commission_rate);
      if (isNaN(rate) || rate < 0 || rate > 100) {
        newErrors.commission_rate = 'Commission rate must be between 0 and 100';
      }
    } else if (formData.commission_type === 'fixed') {
      const amount = parseFloat(formData.fixed_commission_amount);
      if (isNaN(amount) || amount < 0) {
        newErrors.fixed_commission_amount = 'Fixed commission amount cannot be negative';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSaving(true);
    try {
      // Convert snake_case to camelCase for backend
      const submitData = {
        fullName: formData.full_name,
        phone: formData.phone,
        email: formData.email || undefined,
        address: formData.address || undefined,
        commissionType: formData.commission_type,
        commissionRate: parseFloat(formData.commission_rate) || 0,
        fixedCommissionAmount: parseFloat(formData.fixed_commission_amount) || 0,
        bankName: formData.bank_name || undefined,
        accountNumber: formData.account_number || undefined,
        accountName: formData.account_name || undefined,
        isActive: formData.is_active
      };

      if (isEditMode) {
        await updateReferrer(id, submitData);
        toast.success('Referrer updated successfully');
      } else {
        await createReferrer(submitData);
        toast.success('Referrer created successfully');
      }

      navigate('/referrers');
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} referrer`;
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const canEdit = [
    'admin',
    'super_admin',
    'retail_admin',
    'branch_admin',
    'staff',
    'branch_staff'
  ].includes(user?.role);

  if (!canEdit) {
    return (
      <div className="min-h-screen bg-secondary-offWhite flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite">
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/referrers')}
            className="flex items-center gap-2 text-gray-600 hover:text-primary-black mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Referrers
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-primary-black">
            {isEditMode ? 'Edit Referrer' : 'Add New Referrer'}
          </h1>
        </div>

        {/* Duplicate Warning */}
        {duplicates.length > 0 && (
          <div className="bg-yellow-50 border-2 border-yellow-400 p-4 mb-6 rounded-lg shadow-md">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                  Potential Duplicate Referrer{duplicates.length > 1 ? 's' : ''} Found
                </h3>
                <p className="text-sm text-yellow-800 mb-3">
                  {duplicates.length === 1
                    ? 'A similar referrer already exists in the system:'
                    : `${duplicates.length} similar referrers already exist in the system:`}
                </p>

                <div className="space-y-3">
                  {duplicates.map((duplicate, index) => (
                    <div key={index} className="bg-white rounded-lg p-3 border border-yellow-200">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <UserCheck className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-gray-900">{duplicate.full_name}</p>
                              <span className="text-xs font-mono px-2 py-0.5 bg-gray-100 rounded">
                                {duplicate.referrer_code}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">📞 {duplicate.phone}</p>
                            {duplicate.email && (
                              <p className="text-sm text-gray-600 mb-1">✉️ {duplicate.email}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <span
                                className={`text-xs px-2 py-0.5 rounded font-semibold ${
                                  duplicate.match_type === 'exact_phone'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {duplicate.match_type === 'exact_phone'
                                  ? 'Exact Match'
                                  : 'Similar Name'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {Math.round(duplicate.confidence * 100)}% match
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate(`/referrers/${duplicate.id}`)}
                          className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors whitespace-nowrap"
                        >
                          View Details
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 pl-8">{duplicate.reason}</p>
                    </div>
                  ))}
                </div>

                {checkingDuplicates && (
                  <div className="flex items-center gap-2 mt-3 text-sm text-yellow-700">
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Checking for duplicates...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          {/* Personal Information Section */}
          <div>
            <h2 className="text-lg font-semibold text-primary-black mb-4 pb-2 border-b">
              Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent ${
                    errors.full_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter referrer full name"
                />
                {errors.full_name && (
                  <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="08012345678"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="referrer@example.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                  placeholder="Street address"
                />
              </div>
            </div>
          </div>

          {/* Commission Settings Section */}
          <div>
            <h2 className="text-lg font-semibold text-primary-black mb-4 pb-2 border-b">
              Commission Settings
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Commission Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commission Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="commission_type"
                  value={formData.commission_type}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent ${
                    errors.commission_type ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
                {errors.commission_type && (
                  <p className="text-red-500 text-sm mt-1">{errors.commission_type}</p>
                )}
              </div>

              {/* Commission Rate (Percentage) */}
              {formData.commission_type === 'percentage' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commission Rate (%) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="commission_rate"
                    value={formData.commission_rate}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    step="0.01"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent ${
                      errors.commission_rate ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="5.00"
                  />
                  {errors.commission_rate && (
                    <p className="text-red-500 text-sm mt-1">{errors.commission_rate}</p>
                  )}
                </div>
              )}

              {/* Fixed Commission Amount */}
              {formData.commission_type === 'fixed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fixed Commission Amount (₦) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="fixed_commission_amount"
                    value={formData.fixed_commission_amount}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent ${
                      errors.fixed_commission_amount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.00"
                  />
                  {errors.fixed_commission_amount && (
                    <p className="text-red-500 text-sm mt-1">{errors.fixed_commission_amount}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Bank Information Section */}
          <div>
            <h2 className="text-lg font-semibold text-primary-black mb-4 pb-2 border-b">
              Bank Information (Optional)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Bank Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                <input
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                  placeholder="e.g., First Bank"
                />
              </div>

              {/* Account Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number
                </label>
                <input
                  type="text"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                  placeholder="0123456789"
                />
              </div>

              {/* Account Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
                <input
                  type="text"
                  name="account_name"
                  value={formData.account_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                  placeholder="Account holder name"
                />
              </div>
            </div>
          </div>

          {/* Active Status */}
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-accent-gold border-gray-300 rounded focus:ring-accent-gold"
              />
              <span className="text-sm font-medium text-gray-700">Active Referrer</span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-8">
            <button
              type="button"
              onClick={() => navigate('/referrers')}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEditMode ? 'Update Referrer' : 'Create Referrer'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReferrerFormPage;
